// src/components/ui/LoadingStates.jsx
//
// Shared loading + error + empty UI primitives used across the messenger
// pages. Drop-in components; consume the existing CSS keyframe injected
// once at module load.

import React from "react";
import { AlertTriangle, RotateCw, Inbox } from "lucide-react";

const NEON = "#00FF99";

// Inject the shimmer keyframe once when this module loads.
if (typeof document !== "undefined" && !document.getElementById("kwery-shimmer-style")) {
  const s = document.createElement("style");
  s.id = "kwery-shimmer-style";
  s.textContent = `
    @keyframes shimmer {
      0%   { background-position: -200% 0; }
      100% { background-position:  200% 0; }
    }
  `;
  document.head.appendChild(s);
}

const shimmer = {
  background:        "linear-gradient(90deg, #111 25%, #1a1a1a 50%, #111 75%)",
  backgroundSize:    "200% 100%",
  animation:         "shimmer 1.5s linear infinite",
  borderRadius:      4,
};

// ─────────────────────────────────────────────────────────
//  <Skeleton /> — primitive block
// ─────────────────────────────────────────────────────────
export function Skeleton({ width = "100%", height = 14, radius = 4, style = {} }) {
  return (
    <div style={{
      ...shimmer, borderRadius: radius,
      width, height, ...style,
    }} />
  );
}

// ─────────────────────────────────────────────────────────
//  <SkeletonCard /> — a typical dark card with 3 lines
// ─────────────────────────────────────────────────────────
export function SkeletonCard({ lines = 3, height = "auto" }) {
  return (
    <div style={{
      background: "#0a0a0a", border: "0.5px solid #1a1a1a",
      borderRadius: 12, padding: "18px 20px",
      height,
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <Skeleton width="40%" height={12} />
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={`${100 - i * 12}%`} height={10} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  <SkeletonGrid /> — N skeleton cards in a grid
// ─────────────────────────────────────────────────────────
export function SkeletonGrid({ count = 6, cols = 3, lines = 3 }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gap: 14,
    }}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} lines={lines} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  <ErrorState /> — generic error card with retry
// ─────────────────────────────────────────────────────────
export function ErrorState({
  message = "Something went wrong",
  detail = null,
  onRetry = null,
}) {
  return (
    <div style={{
      background: "rgba(226,75,74,0.06)",
      border: "0.5px solid rgba(226,75,74,0.25)",
      borderRadius: 12, padding: "20px 24px",
      display: "flex", alignItems: "center", gap: 16,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        background: "rgba(226,75,74,0.1)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#E24B4A", flexShrink: 0,
      }}>
        <AlertTriangle size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: "#fff", fontWeight: 500 }}>{message}</div>
        {detail && (
          <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{detail}</div>
        )}
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 16px",
            background: "transparent",
            border: "0.5px solid rgba(226,75,74,0.4)",
            borderRadius: 8, color: "#E24B4A",
            fontSize: 12, cursor: "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(226,75,74,0.08)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        >
          <RotateCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  <EmptyState /> — generic empty card
// ─────────────────────────────────────────────────────────
export function EmptyState({
  icon: Icon = Inbox,
  title = "Nothing here yet",
  body = null,
  action = null,
}) {
  return (
    <div style={{
      background: "#0a0a0a", border: "0.5px solid #1a1a1a",
      borderRadius: 14, padding: "48px 32px", textAlign: "center",
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: "50%",
        background: "#0d0d0d", border: "0.5px solid #1e1e1e",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: "#444", marginBottom: 16,
      }}>
        <Icon size={28} />
      </div>
      <div style={{ fontSize: 15, color: "#fff", marginBottom: body ? 6 : 0 }}>{title}</div>
      {body && (
        <div style={{ fontSize: 13, color: "#666", lineHeight: 1.6, maxWidth: 360, margin: "0 auto" }}>
          {body}
        </div>
      )}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

export default { Skeleton, SkeletonCard, SkeletonGrid, ErrorState, EmptyState };
